import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Button from '../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {DropdownMenuCompactRef} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {qrModalVisibleSelector} from '../../../store/selectors/modal';
import {updateQrModal} from '../../../store/actions/modal.actions';

import './menuBar.scss';
import {useFullscreen} from '../../../hooks/useFullscreen';
import {openEndPresentationAction} from '../../../store/actions/chalkBoardElements.actions';
import {removeCurrentSlideDrawingAction} from '../../../store/actions/runfile.actions';
import {toggleDrawEnabledAction} from '../../../store/actions/dashboard.actions';
import {drawEnabledSelector} from '../../../store/selectors/presentation.selectors';

interface MenuBarProps {
    presContainerRef: React.RefObject<HTMLDivElement>;
}

function MenuBar({presContainerRef}: MenuBarProps): JSX.Element {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {isFullscreen, toggleFullscreen} = useFullscreen(presContainerRef);

    const drawEnabled = useSelector(drawEnabledSelector);
    const isQrModalVisible = useSelector(qrModalVisibleSelector);

    const [menuBarOpen, setMenuBarOpen] = useState<boolean>(false);

    const fileMenuItem = useRef<DropdownMenuCompactRef>(null);
    const editMenuItem = useRef<DropdownMenuCompactRef>(null);
    const viewMenuItem = useRef<DropdownMenuCompactRef>(null);

    const menus = [fileMenuItem, editMenuItem, viewMenuItem];

    function closeAllMenusExcept(except?: React.RefObject<DropdownMenuCompactRef>): void {
        menus.forEach((el) => {
            if (el !== except) el.current?.closeMenu();
        });
    }

    function openQrModal(): void {
        dispatch(updateQrModal(true));
    }

    return (
        <div className="c-menuBar">
            <DropdownMenuCompact
                ref={fileMenuItem}
                openOnMouseEnter={menuBarOpen}
                onHoverEnter={() => closeAllMenusExcept(fileMenuItem)}
                onMenuClose={() => {
                    setMenuBarOpen(false);
                }}
                items={[
                    [
                        {
                            label: isFullscreen ? t('header.exitFullscreen') : t('header.fullscreen'),
                            icon: isFullscreen ? 'solar:quit-full-screen-bold' : 'solar:full-screen-bold',
                            onClick: () => toggleFullscreen(),
                        },
                    ],
                    [
                        {
                            label: t('header.exit'),
                            icon: 'solar:exit-linear',
                            onClick: () => dispatch(openEndPresentationAction()),
                        },
                    ],
                ]}
            >
                <Button size="2xs" square variant="ghost" className="c-menuBar__button" onClick={() => setMenuBarOpen(true)}>
                    {t('menuBar.presentation')}
                </Button>
            </DropdownMenuCompact>
            <DropdownMenuCompact
                ref={editMenuItem}
                openOnMouseEnter={menuBarOpen}
                onHoverEnter={() => closeAllMenusExcept(editMenuItem)}
                onMenuClose={() => {
                    setMenuBarOpen(false);
                }}
                items={[
                    [
                        {
                            label: t('header.draw'),
                            icon: 'streamline:pen-draw',
                            onClick: () => dispatch(toggleDrawEnabledAction()),
                            active: drawEnabled,
                        },
                        {
                            label: t('header.clear'),
                            icon: 'solar:eraser-linear',
                            onClick: () => {
                                dispatch(removeCurrentSlideDrawingAction());
                                dispatch(toggleDrawEnabledAction());
                            },
                            disabled: !drawEnabled,
                        },
                    ],
                ]}
            >
                <Button size="2xs" square variant="ghost" className="c-menuBar__button" onClick={() => setMenuBarOpen(true)}>
                    {t('menuBar.edit')}
                </Button>
            </DropdownMenuCompact>
            <DropdownMenuCompact
                ref={viewMenuItem}
                openOnMouseEnter={menuBarOpen}
                onHoverEnter={() => closeAllMenusExcept(viewMenuItem)}
                onMenuClose={() => {
                    setMenuBarOpen(false);
                }}
                items={[
                    [
                        {
                            label: t('header.qrCode'),
                            icon: 'mdi:qrcode-scan',
                            disabled: isQrModalVisible,
                            onClick: () => openQrModal(),
                        },
                    ],
                ]}
            >
                <Button size="2xs" square variant="ghost" className="c-menuBar__button" onClick={() => setMenuBarOpen(true)}>
                    {t('menuBar.viewers')}
                </Button>
            </DropdownMenuCompact>
        </div>
    );
}

export default MenuBar;
